<template>
  <div class="eil">
    <p class="eil__title">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 15l2 2 4-4"/></svg>
      {{ $t("bookingCharges.invoices.title") }}
      <span class="eil__count">{{ invoices.length }}</span>
    </p>

    <transition-group name="eil-item" tag="div" class="eil__list">
      <div
        v-for="inv in invoices"
        :key="inv.id"
        class="eil__item"
        :class="{ 'eil__item--void': inv.status === 'voided' }"
      >
        <!-- Fila principal: izquierda nombre+fecha · derecha nº+total+acciones -->
        <div class="eil__row">
          <div class="eil__left">
            <p class="eil__client">
              <span class="eil-chip" :class="`eil-chip--${statusClassFor(inv)}`">{{ statusLabelFor(inv) }}</span>
              <span class="eil__name">{{ inv.customer_name || $t("bookingCharges.invoices.noName") }}</span>
            </p>
            <button
              v-if="(inv.concepts && inv.concepts.length)"
              class="eil__detail-toggle"
              @click="toggle(inv.id)"
            >
              {{ formatDate(inv.created_at) }}
              <span class="eil__dot">·</span>
              {{ $tc("bookingCharges.invoices.conceptCount", inv.concepts.length, { count: inv.concepts.length }) }}
              <svg class="eil__chev" :class="{ 'eil__chev--open': expanded[inv.id] }" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            <span v-else class="eil__date-plain">{{ formatDate(inv.created_at) }}</span>
          </div>

          <div class="eil__right">
            <div class="eil__amount">
              <span v-if="inv.display_code" class="eil-code">{{ inv.display_code }}</span>
              <span class="eil__total">{{ inv.currency }} {{ formatNum(inv.total) }}</span>
            </div>
            <div class="eil__actions">
              <a v-if="inv.pdf_url" :href="inv.pdf_url" target="_blank" class="eil-ico" :title="$t('bookingCharges.invoices.viewPdf')">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              </a>
              <button v-if="canInvoice && inv.status === 'sent'" class="eil-ico eil-ico--wa" @click="$emit('whatsapp', inv)" :title="$t('bookingCharges.invoices.sendWhatsapp')">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </button>
              <button v-if="inv.status === 'sent' && !inv.is_correction" class="eil-ico" @click="$emit('correct', inv)" :title="$t('bookingCharges.invoices.correct')">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              </button>
              <button v-if="inv.status === 'sent' && !inv.is_correction" class="eil-ico eil-ico--danger" @click="$emit('void', inv)" :title="$t('bookingCharges.invoices.void')">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Detalle: conceptos ya facturados en ESTA factura -->
        <transition name="eil-expand">
          <div v-if="expanded[inv.id] && inv.concepts && inv.concepts.length" class="eil__concepts">
            <div v-for="(c, i) in inv.concepts" :key="i" class="eil__concept">
              <span class="eil__concept-name">
                {{ c.description }}
                <span v-if="c.date" class="eil__concept-date">{{ c.date }}</span>
              </span>
              <span class="eil__concept-amount" :class="{ 'eil__concept-amount--neg': c.discount }">
                {{ inv.currency }} {{ formatNum(c.total_with_vat) }}
              </span>
            </div>
            <p class="eil__concepts-foot">{{ $t("bookingCharges.invoices.conceptsFoot") }}</p>
          </div>
        </transition>
      </div>
    </transition-group>
  </div>
</template>

<script>
/**
 * Listado READ-ONLY de facturas emitidas de una reserva, agrupando sus conceptos
 * (clúster). Presentacional: los botones emiten eventos que el padre reenvía al
 * ReservationInvoicePanel (que tiene los modales). Cada factura trae `concepts`
 * (líneas ya facturadas) para que no dupliquen la tabla de cargos.
 */
export default {
  name: 'EmittedInvoicesList',
  props: {
    invoices:   { type: Array,   default: () => [] },
    canInvoice: { type: Boolean, default: false },
  },
  data() {
    return { expanded: {} };
  },
  methods: {
    toggle(id) { this.$set(this.expanded, id, !this.expanded[id]); },
    statusClassFor(inv) {
      const map = { draft: 'draft', sent: 'sent', processing: 'processing', pending: 'processing', error: 'error', corrected: 'corrected', voided: 'neutral' };
      return map[inv?.status] ?? 'neutral';
    },
    statusLabelFor(inv) {
      const map = {
        draft: this.$t('bookingCharges.status.draft'),
        sent: this.$t('bookingCharges.status.sent'),
        processing: this.$t('bookingCharges.status.processing'),
        pending: this.$t('bookingCharges.status.pending'),
        error: this.$t('bookingCharges.status.error'),
        corrected: this.$t('bookingCharges.status.corrected'),
        voided: this.$t('bookingCharges.status.voided'),
      };
      return map[inv?.status] ?? inv?.status ?? '—';
    },
    formatNum(n) {
      return parseFloat(n || 0).toLocaleString('es-ES', { useGrouping: "always", minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
    },
  },
};
</script>

<style scoped>
.eil {
  --brand: #5e71e4; --ink: #0f172a; --ink-2: #1e293b; --ink-3: #475569; --ink-4: #94a3b8; --ink-5: #cbd5e1;
  --ink-6: #e2e8f0; --ink-7: #f1f5f9; --ink-8: #f8fafc; --red: #dc2626; --red-lt: #fef2f2;
  --green: #16a34a; --green-lt: #f0fdf4; --blue: #2563eb; --wa: #11b14d;
  font-family: 'DM Sans', system-ui, sans-serif;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15,23,42,.05), 0 2px 8px rgba(15,23,42,.04);
  border-radius: 12px; overflow: hidden; margin-bottom: 14px;
}
.eil__title {
  display: flex; align-items: center; gap: 7px; margin: 0; padding: 10px 16px;
  font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .08em;
  color: var(--ink-4); border-bottom: 1px solid var(--ink-7);
}
.eil__title svg { color: var(--green); }
.eil__count {
  min-width: 17px; height: 17px; padding: 0 5px; display: inline-flex; align-items: center; justify-content: center;
  background: var(--green-lt); color: var(--green); border-radius: 9px; font-size: 10px; font-weight: 800; letter-spacing: 0;
}
.eil__item { border-bottom: 1px solid var(--ink-7); }
.eil__item:last-child { border-bottom: none; }
.eil__item--void { opacity: .58; }

.eil__row { display: flex; align-items: flex-start; gap: 12px; padding: 12px 16px; }
.eil__left { flex: 1; min-width: 0; }
.eil__client { display: flex; align-items: center; gap: 8px; margin: 0; min-width: 0; }
.eil__name { font-size: 13.5px; font-weight: 700; color: var(--ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.eil__detail-toggle {
  display: inline-flex; align-items: center; gap: 5px; margin-top: 4px; padding: 0;
  background: none; border: none; cursor: pointer;
  font-family: inherit; font-size: 11.5px; color: var(--ink-4);
}
.eil__detail-toggle:hover { color: var(--brand); }
.eil__dot { color: var(--ink-5); }
.eil__chev { transition: transform .18s ease; }
.eil__chev--open { transform: rotate(180deg); }
.eil__date-plain { display: block; margin-top: 4px; font-size: 11.5px; color: var(--ink-4); }

.eil__right { display: flex; flex-direction: column; align-items: flex-end; gap: 8px; flex-shrink: 0; }
.eil__amount { display: flex; align-items: center; gap: 8px; }
.eil-code {
  font-size: 12px; font-weight: 700; color: var(--ink-3); font-variant-numeric: tabular-nums;
  padding: 2px 8px; background: var(--ink-7); border-radius: 6px;
}
.eil__total { font-size: 15px; font-weight: 800; color: var(--ink); white-space: nowrap; }
.eil__actions { display: flex; align-items: center; gap: 5px; }

.eil-chip {
  display: inline-flex; align-items: center; padding: 2px 8px; flex-shrink: 0;
  border-radius: 20px; font-size: 10.5px; font-weight: 700; white-space: nowrap;
}
.eil-chip--sent { background: var(--green-lt); color: var(--green); }
.eil-chip--corrected { background: #fef3c7; color: #92400e; }
.eil-chip--neutral { background: var(--ink-7); color: var(--ink-3); }
.eil-chip--processing { background: #eff6ff; color: var(--blue); }
.eil-chip--error { background: var(--red-lt); color: var(--red); }
.eil-chip--draft { background: #fff7ed; color: #ea580c; }

.eil-ico {
  display: inline-flex; align-items: center; justify-content: center; width: 29px; height: 29px;
  border: none; cursor: pointer; background: var(--ink-7); color: var(--ink-3);
  border-radius: 8px; text-decoration: none; transition: background .15s, color .15s;
}
.eil-ico:hover { background: var(--ink-6); color: var(--ink); }
.eil-ico--wa:hover { background: rgba(17,177,77,.12); color: var(--wa); }
.eil-ico--danger:hover { background: var(--red-lt); color: var(--red); }

/* Detalle de conceptos ya facturados */
.eil__concepts {
  overflow: hidden;
  margin: 0 16px 12px; padding: 10px 12px;
  background: var(--ink-8); border-radius: 9px;
}
.eil__concept {
  display: flex; align-items: baseline; justify-content: space-between; gap: 10px;
  padding: 4px 0; font-size: 12px;
}
.eil__concept-name { color: var(--ink-2); min-width: 0; }
.eil__concept-date { margin-left: 6px; font-size: 10.5px; color: var(--ink-4); }
.eil__concept-amount { color: var(--ink-2); font-weight: 600; white-space: nowrap; font-variant-numeric: tabular-nums; }
.eil__concept-amount--neg { color: var(--green); }
.eil__concepts-foot { margin: 6px 0 0; font-size: 10.5px; color: var(--ink-4); line-height: 1.4; }

.eil-expand-enter-active, .eil-expand-leave-active { transition: opacity .18s ease; }
.eil-expand-enter, .eil-expand-leave-to { opacity: 0; }

.eil-item-enter-active { transition: opacity .45s ease, transform .45s cubic-bezier(0.16, 1, 0.3, 1); }
.eil-item-enter { opacity: 0; transform: translateY(-12px); }
.eil-item-move { transition: transform .45s cubic-bezier(0.16, 1, 0.3, 1); }

@media (max-width: 460px) {
  .eil__row { flex-direction: column; }
  .eil__right { align-items: flex-start; width: 100%; flex-direction: row; justify-content: space-between; }
}
@media (prefers-reduced-motion: reduce) {
  .eil__chev, .eil-expand-enter-active, .eil-expand-leave-active,
  .eil-item-enter-active, .eil-item-move { transition: none; }
  .eil-item-enter { transform: none; }
}
</style>
